import { cloneElement, isValidElement, useState, useEffect, useId, useCallback } from 'react';
import { apiFetch } from 'api/client';
import { useFocusTrap } from '_components';
import './admin.css';

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [userOrgsMap, setUserOrgsMap] = useState({}); // { [userId]: number[] }
  const [metricCustomerMappings, setMetricCustomerMappings] = useState([]);
  const [error, setError] = useState(null);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'user',
    active: true,
    organization: ''
  });
  const [isEditUserDrawerOpen, setIsEditUserDrawerOpen] = useState(false);
  const [editUserDetails, setEditUserDetails] = useState(null);
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isAddUserDrawerOpen, setIsAddUserDrawerOpen] = useState(false);
  const [isMetricMappingDrawerOpen, setIsMetricMappingDrawerOpen] = useState(false);
  const [newMetricMapping, setNewMetricMapping] = useState({
    organization_id: '',
    customer_id: ''
  });
  const [passwordDetails, setPasswordDetails] = useState({ newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');

  // Assign Users to Organization drawer state
  const [isAssignDrawerOpen, setIsAssignDrawerOpen] = useState(false);
  const [assignOrg, setAssignOrg] = useState(null); // { id, name }
  const [assignOrgUsers, setAssignOrgUsers] = useState([]); // users currently in org
  const [assignSelectedUserId, setAssignSelectedUserId] = useState('');

  const closeEditUserDrawer = useCallback(() => {
    setIsEditUserDrawerOpen(false);
    setEditUserDetails(null);
    setPasswordDetails({ newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordMessage('');
    setIsDeleteConfirmVisible(false);
    setUserToDelete(null);
  }, []);

  const closeAddUserDrawer = useCallback(() => {
    setIsAddUserDrawerOpen(false);
  }, []);

  const closeMetricMappingDrawer = useCallback(() => {
    setIsMetricMappingDrawerOpen(false);
    setNewMetricMapping({ organization_id: '', customer_id: '' });
  }, []);

  const closeAssignDrawer = useCallback(() => {
    setIsAssignDrawerOpen(false);
    setAssignOrg(null);
    setAssignOrgUsers([]);
    setAssignSelectedUserId('');
  }, []);

  // Close any open drawer on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (isDeleteConfirmVisible) { setIsDeleteConfirmVisible(false); setUserToDelete(null); return; }
      if (isEditUserDrawerOpen) { closeEditUserDrawer(); return; }
      if (isAddUserDrawerOpen) { closeAddUserDrawer(); return; }
      if (isMetricMappingDrawerOpen) { closeMetricMappingDrawer(); return; }
      if (isAssignDrawerOpen) { closeAssignDrawer(); return; }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    closeAddUserDrawer,
    closeAssignDrawer,
    closeEditUserDrawer,
    closeMetricMappingDrawer,
    isAddUserDrawerOpen,
    isAssignDrawerOpen,
    isDeleteConfirmVisible,
    isEditUserDrawerOpen,
    isMetricMappingDrawerOpen,
  ]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await apiFetch('/auth/get-users', {
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error(`Error fetching users: ${response.statusText}`);
        }

        const data = await response.json();
        setUsers(data.users);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError(error.message);
      }
    };

    const fetchOrganizations = async () => {
        try {
          const response = await apiFetch('/auth/get-organizations', {
            method: 'GET',
          });

          if (!response.ok) {
            throw new Error(`Error fetching organizations: ${response.statusText}`);
          }
          const data = await response.json();
          setOrganizations(data.organizations);
        } catch (error) {
          console.error('Error fetching organizations:', error);
          setError(error.message);
        }
      };

    const fetchUserOrganizations = async () => {
      try {
        const response = await apiFetch('/auth/user-organizations', {
          method: 'GET',
        });
        if (!response.ok) {
          throw new Error(`Error fetching user-organizations: ${response.statusText}`);
        }
        const data = await response.json();
        // Expecting data like: [{ userid, organizationid }, ...]
        const map = {};
        const list = Array.isArray(data) ? data : (Array.isArray(data.userOrganizations) ? data.userOrganizations : []);
        list.forEach(item => {
          const uid = item.userId || item.userid || item.userID || item.user_id;
          const oid = item.organizationId || item.organizationid || item.organizationID || item.organization_id;
          if (uid == null || oid == null) return;
          if (!map[uid]) map[uid] = [];
          if (!map[uid].includes(oid)) map[uid].push(oid);
        });
        setUserOrgsMap(map);
      } catch (error) {
        console.error('Error fetching user-organizations:', error);
        // Non-fatal: continue without organizations mapping
      }
    };

    const fetchMetricCustomerMappings = async () => {
      try {
        const response = await apiFetch('/auth/metric-customer-mappings', {
          method: 'GET',
        });
        if (!response.ok) {
          throw new Error(await responseErrorMessage(response, 'Failed to load telemetry customer mappings'));
        }
        const data = await response.json();
        setMetricCustomerMappings(Array.isArray(data.mappings) ? data.mappings : []);
      } catch (error) {
        console.error('Error fetching telemetry customer mappings:', error);
        setError(error.message);
      }
    };

    fetchUsers();
    fetchOrganizations();
    fetchUserOrganizations();
    fetchMetricCustomerMappings();
  }, [setUserOrgsMap]); // Empty dependency array ensures this runs only once

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const addUser = async (e) => {
    e.preventDefault();
    try {
      const response = await apiFetch('/auth/register', {
        method: 'POST',
        json: newUser
      });

      if (!response.ok) {
        throw new Error(`Error adding user: ${response.statusText}`);
      }

      const data = await response.json();
      setUsers([...users, data.user]);
      setNewUser({
        username: '',
        password: '',
        role: 'user',
        active: true,
        organization: ''
      });
      closeAddUserDrawer();
    } catch (error) {
      console.error('Error adding user:', error);
      setError(error.message);
    }
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    const { id, username } = userToDelete;
    try {
      const response = await apiFetch('/auth/del-user', {
        method: 'DELETE',
        json: { id, username }
      });

      if (!response.ok) {
        throw new Error(`Error deleting user: ${response.statusText}`);
      }

      // Remove user from state
      setUsers(users.filter(user => user.id !== id));
      closeEditUserDrawer();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError(error.message);
    }
  };

  const confirmDeleteUser = (user) => {
    setUserToDelete(user);
    setIsDeleteConfirmVisible(true);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordDetails({ ...passwordDetails, [name]: value });
    setPasswordError('');
    setPasswordMessage('');
  };

  const changePassword = async () => {
    const { newPassword, confirmPassword } = passwordDetails;
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      setPasswordMessage('');
      return;
    }

    setPasswordError('');
    setPasswordMessage('');

    const { id, username } = editUserDetails;
    try {
      const response = await apiFetch('/auth/set-password', {
        method: 'POST',
        json: { id, username, newpassword: newPassword }
      });

      if (!response.ok) {
        setPasswordError('Error changing password');
        return;
      }

      // Update user password in state
      setUsers(users.map(user => user.id === id ? { ...user, password: newPassword } : user));
      setPasswordDetails({ newPassword: '', confirmPassword: '' });
      setPasswordMessage('Password changed successfully');
    } catch (error) {
      console.error('Error changing password:', error);
      setError(error.message);
    }
  };

  const editUser = (id) => {
    const user = users.find(user => user.id === id);
    setEditUserDetails(user);
    setPasswordDetails({ newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordMessage('');
    setIsDeleteConfirmVisible(false);
    setUserToDelete(null);
    setIsEditUserDrawerOpen(true);
  };

  // Helpers for organizations
  const orgIdToName = (id) => {
    const org = organizations.find(o => String(o.id) === String(id));
    return org ? (org.name || org.displayName || org.id) : id;
  };

  const organizationNamesForUser = (user) => {
    if (user.organizations && user.organizations.length > 0) {
      return user.organizations.map(o => o.name || o.displayName || o.id).join(', ');
    }

    const organizationIds = userOrgsMap[user.id] || [];
    if (organizationIds.length > 0) {
      return organizationIds.map(orgIdToName).join(', ');
    }

    return '';
  };

  const removeUserFromOrg = async (userId, orgId) => {
    try {
      const response = await apiFetch(`/auth/user-organization/${userId}/${orgId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`Failed to remove user from organization`);
      }
      setUserOrgsMap(prev => {
        const next = { ...prev };
        next[userId] = (next[userId] || []).filter(id => String(id) !== String(orgId));
        return next;
      });
      // If assign drawer is open for this org, update its list, too
      if (isAssignDrawerOpen && assignOrg && String(assignOrg.id) === String(orgId)) {
        setAssignOrgUsers(prev => prev.filter(u => String(u.id) !== String(userId)));
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const addUserToOrg = async (userId, orgId) => {
    try {
      const response = await apiFetch('/auth/user-organization', {
        method: 'POST',
        json: { user_id: parseInt(userId, 10), organization_id: parseInt(orgId, 10) }
      });
      if (response.status === 409) {
        // Already a member: sync the UI to reflect that without showing an error.
        const user = users.find(u => String(u.id) === String(userId));
        if (user && isAssignDrawerOpen && assignOrg && String(assignOrg.id) === String(orgId)) {
          setAssignOrgUsers(prev => prev.some(u => String(u.id) === String(userId)) ? prev : [...prev, user]);
        }
        return;
      }
      if (!response.ok) {
        throw new Error('Failed to add user to organization');
      }
      setUserOrgsMap(prev => {
        const next = { ...prev };
        const arr = new Set([...(next[userId] || []), orgId]);
        next[userId] = Array.from(arr);
        return next;
      });
      if (isAssignDrawerOpen && assignOrg && String(assignOrg.id) === String(orgId)) {
        const user = users.find(u => String(u.id) === String(userId));
        if (user) setAssignOrgUsers(prev => [...prev, user]);
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const openAssignDrawer = async (org) => {
    setAssignOrg(org);
    setIsAssignDrawerOpen(true);
    try {
      const response = await apiFetch(`/auth/organization/${org.id}/users`, {
        method: 'GET',
      });
      if (!response.ok) throw new Error('Failed to load organization users');
      const data = await response.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data.users) ? data.users : []);
      setAssignOrgUsers(list);
      setAssignSelectedUserId('');
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const openMetricMappingDrawer = () => {
    setNewMetricMapping({
      organization_id: organizations.length > 0 ? String(organizations[0].id) : '',
      customer_id: ''
    });
    setIsMetricMappingDrawerOpen(true);
  };

  const createMetricCustomerMapping = async (event) => {
    event.preventDefault();
    try {
      const response = await apiFetch('/auth/metric-customer-mappings', {
        method: 'POST',
        json: {
          organization_id: Number(newMetricMapping.organization_id),
          customer_id: newMetricMapping.customer_id.trim()
        }
      });
      if (!response.ok) {
        throw new Error(await responseErrorMessage(response, 'Failed to create telemetry customer mapping'));
      }
      const data = await response.json();
      setMetricCustomerMappings(previous => [...previous, data.mapping].sort((left, right) => {
        const organizationOrder = String(left.organization_name).localeCompare(String(right.organization_name));
        return organizationOrder || String(left.customer_id).localeCompare(String(right.customer_id));
      }));
      closeMetricMappingDrawer();
    } catch (mappingError) {
      console.error('Error creating telemetry customer mapping:', mappingError);
      setError(mappingError.message);
    }
  };

  const deleteMetricCustomerMapping = async (mapping) => {
    if (!window.confirm(`Remove customer ID "${mapping.customer_id}" from ${mapping.organization_name}?`)) {
      return;
    }
    try {
      const response = await apiFetch(`/auth/metric-customer-mappings/${mapping.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(await responseErrorMessage(response, 'Failed to delete telemetry customer mapping'));
      }
      setMetricCustomerMappings(previous => previous.filter(item => item.id !== mapping.id));
    } catch (mappingError) {
      console.error('Error deleting telemetry customer mapping:', mappingError);
      setError(mappingError.message);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditUserDetails({ ...editUserDetails, [name]: value });
  };

  const saveUserDetails = async () => {
    try {
      const response = await apiFetch('/auth/update-user-details', {
        method: 'POST',
        json: editUserDetails
      });

      if (!response.ok) {
        throw new Error(`Error updating user: ${response.statusText}`);
      }

      const updatedUser = await response.json();
      setUsers(users.map(user => user.id === updatedUser.user.id ? updatedUser.user : user));
      closeEditUserDrawer();
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error.message);
    }
  };

  return (
    <div className="admin-page">

      {/* ── Hero header ──────────────────────────────────────── */}
      <div className="admin-hero">
        <div>
          <div className="admin-breadcrumb">
            <span className="admin-breadcrumb-dot"></span>
            <span className="admin-breadcrumb-text">System / Administration</span>
          </div>
          <h1 className="admin-title">Admin</h1>
        </div>
      </div>

      {error && (
        <div className="admin-error-banner">
          <span className="material-symbols-outlined">error</span>
          {error}
          <button onClick={() => setError(null)} className="admin-error-close">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      )}

      {/* ── Users section ────────────────────────────────────── */}
      <div className="lists-table-card" style={{ marginBottom: '1.5rem' }}>
        <div className="lists-table-header">
          <div className="flex items-center gap-3">
            <h2 className="lists-table-title">Users</h2>
            <span className="lists-badge secondary">{users.length} Total</span>
          </div>
          <button className="lists-create-btn" onClick={() => setIsAddUserDrawerOpen(true)}>
            <span className="material-symbols-outlined text-sm">person_add</span>
            Add User
          </button>
        </div>
        <div className="lists-table-wrap">
          <table className="lists-table">
            <thead>
              <tr className="lists-table-head-row">
                <th className="lists-th">ID</th>
                <th className="lists-th">Username</th>
                <th className="lists-th">Role</th>
                <th className="lists-th">Active</th>
                <th className="lists-th">Organizations</th>
                <th className="lists-th"></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={6} className="lists-td lists-empty-cell">No users found</td></tr>
              )}
              {users.map(user => (
                <tr key={user.id} className="lists-table-row" style={{ cursor: 'default' }}>
                  <td className="lists-td"><span className="lists-row-time">{user.id}</span></td>
                  <td className="lists-td"><span className="lists-row-name">{user.username}</span></td>
                  <td className="lists-td">
                    <span className={`lists-status-pill ${user.role === 'admin' ? 'active' : 'inactive'}`}>{user.role}</span>
                  </td>
                  <td className="lists-td">
                    <span className={`lists-status-pill ${user.active ? 'active' : 'inactive'}`}>
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="lists-td">
                    <span className="lists-row-sync">
                      {organizationNamesForUser(user) || <span style={{ opacity: 0.4 }}>—</span>}
                    </span>
                  </td>
                  <td className="lists-td text-right">
                    <button className="lists-icon-btn" title="Edit user" onClick={() => editUser(user.id)}>
                      <span className="material-symbols-outlined">edit</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Organizations section ──────────────────────────────── */}
      <div className="lists-table-card">
        <div className="lists-table-header">
          <div className="flex items-center gap-3">
            <h2 className="lists-table-title">Organizations</h2>
            <span className="lists-badge secondary">{organizations.length} Total</span>
          </div>
        </div>
        <div className="lists-table-wrap">
          <table className="lists-table">
            <thead>
              <tr className="lists-table-head-row">
                <th className="lists-th">ID</th>
                <th className="lists-th">Name</th>
                <th className="lists-th">Token</th>
                <th className="lists-th"></th>
              </tr>
            </thead>
            <tbody>
              {organizations.length === 0 && (
                <tr><td colSpan={4} className="lists-td lists-empty-cell">No organizations found</td></tr>
              )}
              {organizations.map(org => (
                <tr key={org.id} className="lists-table-row" style={{ cursor: 'default' }}>
                  <td className="lists-td"><span className="lists-row-time">{org.id}</span></td>
                  <td className="lists-td"><span className="lists-row-name">{org.name}</span></td>
                  <td className="lists-td"><code className="lists-row-desc" style={{ fontFamily: 'monospace' }}>{org.token || '—'}</code></td>
                  <td className="lists-td text-right">
                    <button className="lists-icon-btn" title="Assign users" onClick={() => openAssignDrawer(org)}>
                      <span className="material-symbols-outlined">group_add</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── External telemetry customer mappings ──────────────── */}
      <div className="lists-table-card" style={{ marginTop: '1.5rem' }}>
        <div className="lists-table-header">
          <div className="flex items-center gap-3">
            <h2 className="lists-table-title">Telemetry Customer Mappings</h2>
            <span className="lists-badge secondary">{metricCustomerMappings.length} Total</span>
          </div>
          <button
            className="lists-create-btn"
            onClick={openMetricMappingDrawer}
            disabled={organizations.length === 0}
          >
            <span className="material-symbols-outlined text-sm">add_link</span>
            Add Mapping
          </button>
        </div>
        <div className="lists-table-wrap">
          <table className="lists-table">
            <thead>
              <tr className="lists-table-head-row">
                <th className="lists-th">Organization</th>
                <th className="lists-th">Organization ID</th>
                <th className="lists-th">Customer ID</th>
                <th className="lists-th"></th>
              </tr>
            </thead>
            <tbody>
              {metricCustomerMappings.length === 0 && (
                <tr>
                  <td colSpan={4} className="lists-td lists-empty-cell">
                    No legacy telemetry customer IDs configured.
                  </td>
                </tr>
              )}
              {metricCustomerMappings.map(mapping => (
                <tr key={mapping.id} className="lists-table-row" style={{ cursor: 'default' }}>
                  <td className="lists-td">
                    <span className="lists-row-name">{mapping.organization_name}</span>
                  </td>
                  <td className="lists-td">
                    <span className="lists-row-time">{mapping.organization_id}</span>
                  </td>
                  <td className="lists-td">
                    <code className="lists-row-desc" style={{ fontFamily: 'monospace' }}>
                      {mapping.customer_id}
                    </code>
                  </td>
                  <td className="lists-td text-right">
                    <button
                      className="lists-icon-btn danger"
                      title="Remove telemetry customer mapping"
                      onClick={() => deleteMetricCustomerMapping(mapping)}
                    >
                      <span className="material-symbols-outlined">link_off</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AdminDrawer
        open={isEditUserDrawerOpen && !!editUserDetails}
        eyebrow="User Access"
        title={editUserDetails ? `Edit User - ${editUserDetails.username}` : 'Edit User'}
        onClose={closeEditUserDrawer}
        size="lg"
        footer={(
          <button type="button" className="admin-drawer-secondary-btn" onClick={closeEditUserDrawer}>
            Close
          </button>
        )}
      >
        {passwordError && (
          <div className="admin-error-banner admin-drawer-message">
            <span className="material-symbols-outlined">error</span>{passwordError}
          </div>
        )}
        {passwordMessage && (
          <div className="admin-success-banner admin-drawer-message">
            <span className="material-symbols-outlined">check_circle</span>{passwordMessage}
          </div>
        )}
        {editUserDetails && (
          <div className="admin-edit-grid">
            <div className="admin-edit-col">
              <p className="admin-section-label">User Details</p>
              <AdminField label="Username">
                <input className="profile-input" name="username" value={editUserDetails.username}
                  onChange={handleEditInputChange} required />
              </AdminField>
              <AdminField label="Role">
                <select className="profile-select" name="role" value={editUserDetails.role}
                  onChange={handleEditInputChange}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </AdminField>
              <label className="lists-toggle-label">
                <input type="checkbox" className="lists-toggle-input"
                  checked={!!editUserDetails.active}
                  onChange={e => setEditUserDetails({ ...editUserDetails, active: e.target.checked })} />
                <span className="lists-toggle-track"><span className="lists-toggle-thumb"></span></span>
                <span className="lists-toggle-text">Active</span>
              </label>
              <div className="admin-edit-actions">
                <button type="button" className="profile-btn-primary" onClick={saveUserDetails}>
                  <span className="material-symbols-outlined text-sm">save</span> Save
                </button>
                <button type="button" className="admin-drawer-danger-btn"
                  onClick={() => confirmDeleteUser(editUserDetails)}>
                  <span className="material-symbols-outlined text-sm">delete</span> Delete
                </button>
              </div>
              {isDeleteConfirmVisible && (
                <div className="admin-danger-confirm">
                  <p className="admin-danger-title">Delete user?</p>
                  <p className="admin-danger-copy">
                    Delete <strong>{userToDelete?.username}</strong>? This cannot be undone.
                  </p>
                  <div className="admin-danger-actions">
                    <button
                      type="button"
                      className="admin-drawer-secondary-btn"
                      onClick={() => { setIsDeleteConfirmVisible(false); setUserToDelete(null); }}
                    >
                      Cancel
                    </button>
                    <button type="button" className="admin-drawer-danger-btn solid" onClick={deleteUser}>
                      Delete user
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="admin-edit-col">
              <p className="admin-section-label">Change Password</p>
              <AdminField label="New Password">
                <input className="profile-input" type="password" name="newPassword"
                  value={passwordDetails.newPassword} onChange={handlePasswordChange} />
              </AdminField>
              <AdminField label="Confirm Password">
                <input className="profile-input" type="password" name="confirmPassword"
                  value={passwordDetails.confirmPassword} onChange={handlePasswordChange} />
              </AdminField>
              <button type="button" className="profile-btn-primary" onClick={changePassword}>
                <span className="material-symbols-outlined text-sm">lock_reset</span> Change Password
              </button>
            </div>
          </div>
        )}
      </AdminDrawer>

      <AdminDrawer
        open={isAssignDrawerOpen}
        eyebrow="Organization Membership"
        title={`Assign Users - ${assignOrg?.name || assignOrg?.id || 'Organization'}`}
        onClose={closeAssignDrawer}
        size="md"
        footer={(
          <button type="button" className="admin-drawer-secondary-btn" onClick={closeAssignDrawer}>
            Close
          </button>
        )}
      >
        <p className="admin-section-label">Current Members</p>
        {assignOrgUsers.length === 0 ? (
          <p className="profile-hint">No users in this organization.</p>
        ) : (
          <ul className="admin-user-list">
            {assignOrgUsers.map(u => (
              <li key={u.id} className="admin-org-user-item">
                <span className="lists-row-sync">{u.username} <span style={{ opacity: 0.5 }}>#{u.id}</span></span>
                <button className="lists-icon-btn danger" title="Remove"
                  onClick={() => removeUserFromOrg(u.id, assignOrg.id)}>
                  <span className="material-symbols-outlined">person_remove</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="admin-section-label admin-section-spaced">Add Member</p>
        <div className="admin-assign-row">
          <select className="profile-select"
            value={assignSelectedUserId}
            onChange={e => setAssignSelectedUserId(e.target.value)}>
            <option value="" disabled>Select user...</option>
            {users
              .filter(u => !assignOrgUsers.some(x => String(x.id) === String(u.id)))
              .map(u => (
                <option key={u.id} value={u.id}>{u.username} (#{u.id})</option>
              ))}
          </select>
          <button className="profile-btn-primary"
            disabled={!assignSelectedUserId || !assignOrg}
            onClick={() => { addUserToOrg(assignSelectedUserId, assignOrg.id); setAssignSelectedUserId(''); }}>
            <span className="material-symbols-outlined text-sm">person_add</span> Add
          </button>
        </div>
      </AdminDrawer>

      <AdminDrawer
        open={isMetricMappingDrawerOpen}
        eyebrow="External Telemetry"
        title="Add Customer Mapping"
        onClose={closeMetricMappingDrawer}
        size="sm"
        footer={(
          <>
            <button type="button" className="admin-drawer-secondary-btn" onClick={closeMetricMappingDrawer}>
              Cancel
            </button>
            <button type="submit" form="admin-add-metric-mapping-form" className="admin-drawer-primary-btn">
              <span className="material-symbols-outlined text-sm">add_link</span> Add Mapping
            </button>
          </>
        )}
      >
        <form
          id="admin-add-metric-mapping-form"
          onSubmit={createMetricCustomerMapping}
          className="admin-drawer-form"
        >
          <p className="profile-hint">
            Map a legacy VictoriaMetrics customer_id to the organization allowed to query it.
          </p>
          <AdminField label="Organization" required>
            <select
              className="profile-select"
              value={newMetricMapping.organization_id}
              onChange={event => setNewMetricMapping({
                ...newMetricMapping,
                organization_id: event.target.value
              })}
              required
            >
              <option value="" disabled>Select organization...</option>
              {organizations.map(organization => (
                <option key={organization.id} value={organization.id}>
                  {organization.name} (#{organization.id})
                </option>
              ))}
            </select>
          </AdminField>
          <AdminField label="Customer ID" required>
            <input
              className="profile-input"
              value={newMetricMapping.customer_id}
              onChange={event => setNewMetricMapping({
                ...newMetricMapping,
                customer_id: event.target.value
              })}
              placeholder="web4soft"
              maxLength={255}
              autoComplete="off"
              required
            />
          </AdminField>
        </form>
      </AdminDrawer>

      <AdminDrawer
        open={isAddUserDrawerOpen}
        eyebrow="User Access"
        title="Add User"
        onClose={closeAddUserDrawer}
        size="sm"
        footer={(
          <>
            <button type="button" className="admin-drawer-secondary-btn" onClick={closeAddUserDrawer}>
              Cancel
            </button>
            <button type="submit" form="admin-add-user-form" className="admin-drawer-primary-btn">
              <span className="material-symbols-outlined text-sm">person_add</span> Add User
            </button>
          </>
        )}
      >
        <form id="admin-add-user-form" onSubmit={addUser} className="admin-drawer-form">
          <AdminField label="Username" required>
            <input className="profile-input" name="username" placeholder="username"
              value={newUser.username} onChange={handleInputChange} required />
          </AdminField>
          <AdminField label="Password" required>
            <input className="profile-input" type="password" name="password" placeholder="password"
              value={newUser.password} onChange={handleInputChange} required />
          </AdminField>
          <AdminField label="Organization">
            <input className="profile-input" name="organization" placeholder="optional"
              value={newUser.organization} onChange={handleInputChange} />
          </AdminField>
          <AdminField label="Role">
            <select className="profile-select" name="role" value={newUser.role} onChange={handleInputChange}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </AdminField>
          <label className="lists-toggle-label">
            <input type="checkbox" className="lists-toggle-input"
              checked={newUser.active}
              onChange={e => setNewUser({ ...newUser, active: e.target.checked })} />
            <span className="lists-toggle-track"><span className="lists-toggle-thumb"></span></span>
            <span className="lists-toggle-text">Active</span>
          </label>
        </form>
      </AdminDrawer>
    </div>
  );
};

// Shared off-canvas panel wrapper for Admin workflows.
function AdminDrawer({ open, eyebrow, title, children, footer, onClose, size = 'md' }) {
  const titleId = useId();
  const drawerRef = useFocusTrap(open, onClose, { dismissOnEscape: false });

  if (!open) return null;

  return (
    <div className="admin-drawer-layer">
      <button
        type="button"
        className="admin-drawer-scrim"
        onClick={onClose}
        aria-label="Close panel"
      />
      <aside
        ref={drawerRef}
        className={`admin-drawer admin-drawer-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="admin-drawer-header">
          <div className="admin-drawer-title-wrap">
            {eyebrow && <span className="admin-drawer-eyebrow">{eyebrow}</span>}
            <h2 id={titleId} className="admin-drawer-title">{title}</h2>
          </div>
          <button type="button" className="admin-drawer-close" onClick={onClose} aria-label="Close panel">
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>
        <div className="admin-drawer-content">
          {children}
        </div>
        {footer && (
          <footer className="admin-drawer-footer">
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );
}

// ── Shared field wrapper ──────────────────────────────────────────────────
function AdminField({ label, required, children }) {
  const generatedId = useId();
  const isControl = isValidElement(children)
    && typeof children.type === 'string'
    && ['input', 'select', 'textarea'].includes(children.type);
  const controlId = isControl ? (children.props.id || generatedId) : undefined;
  const fieldControl = isControl ? cloneElement(children, {
    id: controlId,
    required: required || children.props.required,
  }) : children;

  return (
    <div className="profile-field">
      <label className="profile-field-label" htmlFor={controlId}>{label}{required && ' *'}</label>
      {fieldControl}
    </div>
  );
}

export { Admin };

async function responseErrorMessage(response, fallback) {
  try {
    const body = await response.json();
    return body.error || fallback;
  } catch {
    return fallback;
  }
}
